import { DeveloperBoard } from '@mui/icons-material';
import { Box, Button, Card, CardHeader, Tooltip } from '@mui/material';
import { PlcType } from 'attrs/plcConfigValues';
import { useTranslation } from 'react-i18next';
import { NotifyIcon, NotifyTypo } from 'web-components';
import { useSelector } from 'react-redux';
import { getAutomationSystemModbusConfig } from 'redux/machines/selectors';

import withLoadingState from '../../../../helpers/withLoadingState';

const labelAmend = 'machines.detail.automation_system.configuration';

interface AutomationSystemConfigurationCardProps {
  automationSystem: {
    plc_type: string;
    autoconfig_enabled: boolean;
  };
  setDisplayEditAutomationSystemConfigurationModal: React.Dispatch<React.SetStateAction<boolean>>;
  automationSystemConfig: {
    iris_v2_siemens?: {
      address: string;
      port: number;
      rack: number;
      slot: number;
      type: string;
    };
    iris_v2_rockwell?: {
      address: string;
      port: number;
      rack: number;
      slot: number;
      type: string;
    };
    iris_v3_inview?: {
      created_at: string;
      created_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      database_name: string;
      last_updated_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      type: string;
      updated_at: string;
      variables_table_name?: string;
    };
    iris_v3_essentials?: {
      created_at: string;
      created_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      feeding_pump_drive: {
        model: string;
        id?: number;
      };
      last_updated_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      mill_drive: {
        id: number;
        model: string;
      };
      type: string;
      updated_at: string;
    };
    iris_v3_inside_s7?: {
      address: string;
      created_at: string;
      created_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      }
      last_updated_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      }
      port: number;
      rack: number;
      slot: number;
      updated_at: string;
    };
    iris_v3_inside_rockwell?: {
      address: string;
      created_at: string;
      created_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      last_updated_by: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
      };
      slot: number;
      updated_at: string;
    };
  };
}

interface ModbusConfig {
  display_value: string;
  enumeration: string;
}

const AutomationSystemConfigurationCard = ({
  automationSystem,
  setDisplayEditAutomationSystemConfigurationModal,
  automationSystemConfig
}: AutomationSystemConfigurationCardProps) => {
  const { t } = useTranslation();

  const { data: automationSystemModbusConfig } = useSelector(getAutomationSystemModbusConfig);
  const feedingPumpDriveModelNONE = 'NONE';

  const getConfigDisplayName = (enumeration: string, type: string) =>
    automationSystemModbusConfig[type]?.find((config: ModbusConfig) => config?.enumeration === enumeration)
      ?.display_value;

  return (
    <Card sx={{ p: 4, my: 2 }}>
      <CardHeader
        sx={{ p: 0, alignItems: 'flex-start', mb: 0 }}
        action={
          <Tooltip
            title={t('machines.detail.plc_not_filled')}
            disableHoverListener={automationSystem.plc_type !== PlcType.UNKNOWN}
            arrow
          >
            <span>
              <Button
                variant="text"
                disabled={automationSystem.plc_type === PlcType.UNKNOWN}
                sx={{ p: 1.5, gap: 1, display: 'flex', height: '50px', width: '96px' }}
                onClick={() => setDisplayEditAutomationSystemConfigurationModal(true)}
              >
                <NotifyIcon iconName="edit" fontSize="medium" />
                {t('machines.machine_details.edit')}
              </Button>
            </span>
          </Tooltip>
        }
        title={
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ mt: '15px', fontSize: '16px' }}>{t(`${labelAmend}.title`)}</Box>
          </Box>
        }
        titleTypographyProps={{
          fontWeight: '500',
          variant: 'body1'
        }}
      />
      <NotifyTypo.Subtitle1 fontWeight="400" sx={{ mb: '30px' }}>
        {t(`${labelAmend}.subtitle`)}
      </NotifyTypo.Subtitle1>

      {automationSystem.plc_type === PlcType.UNKNOWN ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              borderRadius: '100%',
              backgroundColor: '#EDEDF2',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '20px',
              color: '#CCCCCC'
            }}
          >
            <DeveloperBoard fontSize="large" />
          </div>
          <div>
            <NotifyTypo.Body1 fontWeight="500">
              {t(`${labelAmend}.there_is_no_automation_system_selected`)}
            </NotifyTypo.Body1>
            <NotifyTypo.Subtitle1 fontWeight="400">
              {t(`${labelAmend}.select_automation_system_model_before`)}
            </NotifyTypo.Subtitle1>
          </div>
        </div>
      ) : automationSystem.plc_type === PlcType.SIEMENS_S7 ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.ip_address`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_siemens?.address}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.port`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_siemens?.port}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.rack`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_siemens?.rack}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.slot`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_siemens?.slot}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : automationSystem.plc_type === PlcType.ROCKWELL ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 4">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.ip_address`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_rockwell?.address}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 4">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.slot`)}*</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v2_rockwell?.slot}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : automationSystem.plc_type === PlcType.IRIS_V3 ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 4">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.database_name`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inview?.database_name}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 4">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.variable_table_name`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inview?.variables_table_name}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : automationSystem.plc_type === PlcType.IRIS_V3_ESSENTIALS ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.mill_drive_model`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>
              {automationSystemConfig?.iris_v3_essentials &&
                getConfigDisplayName(automationSystemConfig?.iris_v3_essentials?.mill_drive.model, 'mill')}
            </NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.mill_id`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_essentials?.mill_drive.id}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.feeding_pump_drive_model`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>
              {automationSystemConfig?.iris_v3_essentials &&
                getConfigDisplayName(
                  automationSystemConfig?.iris_v3_essentials?.feeding_pump_drive.model,
                  'feeding_pump'
                )}
            </NotifyTypo.Body1>
          </Box>
          <Box
            display={
              automationSystemConfig?.iris_v3_essentials?.feeding_pump_drive.model === feedingPumpDriveModelNONE
                ? 'none'
                : 'block'
            }
            gridColumn="span 2"
          >
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.feeding_pump_id`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_essentials?.feeding_pump_drive.id}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : automationSystem.plc_type === PlcType.IRIS_V3_INSIDE_S7 ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.ip_address`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_s7?.address}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.port`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_s7?.port}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.rack`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_s7?.rack}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.slot`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_s7?.slot}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : automationSystem.plc_type === PlcType.IRIS_V3_INSIDE_ROCKWELL ? (
        <Box display="grid" gridTemplateColumns="repeat(10, 1fr)" gap={2} sx={{ mt: 3, mb: 4 }}>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.ip_address`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_rockwell?.address}</NotifyTypo.Body1>
          </Box>
          <Box gridColumn="span 2">
            <NotifyTypo.InfoText fontWeight="400">{t(`${labelAmend}.slot`)}</NotifyTypo.InfoText>
            <NotifyTypo.Body1>{automationSystemConfig?.iris_v3_inside_rockwell?.slot}</NotifyTypo.Body1>
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Card>
  );
};

export default withLoadingState(AutomationSystemConfigurationCard);
